import React, {useEffect, useMemo, useState} from 'react';
import ReactGA from 'react-ga';
import {compose} from 'redux';
import {connect} from 'react-redux';
import TagManager from 'react-gtm-module';

import './App.scss';
import Routes from './util/routes';
import {
  addToGlobalStyles,
  setStrings,
  setStringsDefault,
  setBuDefaultFunctionalities,
  setGlobalStyles,
  setTenant,
  setBuDefaultStyles,
  setLanguage,
  setBankAccountDataHolder,
  setIban,
  setTitular,
  setBusFuncMapDefault,
  setIsUnderMaintenance,
  setIsUnderMaintenanceMessage,
} from './store/actions';
import {AuthProvider} from './providers/AuthProvider';
import {
  uriMemberData,
  uriGetFunctionalities,
  uriGetTenants,
  uriGetTitular,
} from './api/endpoints';
import {get} from './api/api';
import AppSpinner from './components/app-spinner';
import {RedirectUrlProvider} from './contexts/RedirectUrlContext';
import {
  isObjectEmpty,
  isRegExpValid,
  updateStringFeatureWithLabels,
} from './util/utility';
import {functionalitiesAliasMap} from './util/strings';
import {getBusInfo} from './api/client';
import PageMaintenance from './components/page-maintenance';

export interface ITenant {
  alias: string;
  alternativeRegistrationURL: string;
  buDefaultAlias: string;
  country: string;
  countryDescription: string;
  footerImage: string;
  id: number;
  languages: any;
  loginFlow: any;
  logoLoginImage: string;
  registerSuccessImage: string;
  spinnerAppGreyBackground: string;
  spinnerAppWhiteBackground: string;
  spinnerPortalGreyBackground: string;
  spinnerPortalWhiteBackground: string;
  title: string;
  vatNumberValidator: string;
  mainLanguageAlias: string;
}

export interface AppProps {
  globalStyles: any;
  addToGlobalStyles(value: any): void;
  setGlobalStyles(value: any): void;
  strings: any;
  selectedBu: any;
  busStylesMap: any;
  buStyles: any;
  user: any;
  loadingInfo: boolean;
  setTenant(value: any): void;
  setLanguage(value: any): void;
  setBankAccountDataHolder(value: any): void;
  setIban(value: any): void;
  setTitular(value: any): void;
  bankAccountDataHolder: any;
  updateRoutesNewSelectedPatient: any;
  busFuncMap: any;
  busFuncMapDefault: any;
  tenant: ITenant;
  language: any;
  setBusFuncMapDefault: Function;
  reduxStrings: any;
  setStrings: Function;
  setStringsDefault: Function;
  setBuDefaultFunctionalities: Function;
  setIsUnderMaintenance: Function;
  setIsUnderMaintenanceMessage: Function;
  isUnderMaintenance: boolean;
}

function App({
  strings,
  globalStyles,
  selectedBu,
  user,
  loadingInfo,
  setTenant,
  setLanguage,
  setBankAccountDataHolder,
  setIban,
  setTitular,
  bankAccountDataHolder,
  updateRoutesNewSelectedPatient,
  busFuncMap,
  busFuncMapDefault,
  tenant,
  language,
  setBusFuncMapDefault,
  reduxStrings,
  setStrings,
  setStringsDefault,
  setBuDefaultFunctionalities,
  setIsUnderMaintenance,
  setIsUnderMaintenanceMessage,
  isUnderMaintenance,
}: AppProps) {
  const [localTenant, setLocalTenant] = useState<ITenant>();
  const [googleAnalyticsGtm, setGoogleAnalyticsGtm] = useState(null);
  const [defaultDependencies, setDefaultDependencies] = useState({
    mainLanguageAlias: '',
    language: '',
  });
  const [dependenciesForTtular, setDependenciesForTitular] = useState({
    bankAccountDataHolder: '',
    selectedBuFamId: '',
  });

  const getMaintenanceMessage = async () => {
    const tenantAlias = process.env.REACT_APP_DEFAULT_TENANT_ALIAS;
    await fetch(uriGetTenants(), {
      method: 'GET',
      headers: {
        X_MYADV_API_KEY: tenantAlias || '',
        advBu: tenant?.buDefaultAlias,
      },
    })
      .then(async (res) => {
        const responseText = await res.text();
        const message = JSON.parse(responseText)?.message;
        setIsUnderMaintenanceMessage(message);
      })
      .catch((err) => {
        console.log('err', err);
      });
  };

  const getTenant = async () => {
    await get(uriGetTenants()).then((resp: any) => {
      const result: ITenant = {
        ...(resp?.data?.items || resp)?.[0],
        loginFlow: eval(`(${(resp?.data?.items || resp)?.[0]?.loginFlow})`),
        vatNumberValidator: isRegExpValid(
          (resp?.data?.items || resp)?.[0]?.vatNumberValidator
        )
          ? (resp?.data?.items || resp)?.[0]?.vatNumberValidator
          : '[\\s\\S]+',
      };
      setLocalTenant(result);
      setTenant(result);
    });
  };

  useEffect(() => {
    setIsUnderMaintenance(null);
    setIsUnderMaintenanceMessage(null);
    getTenant();
  }, []);

  useEffect(() => {
    getMaintenanceMessage();
  }, [isUnderMaintenance]);

  useEffect(() => {
    if (user) {
      setLanguage(user?.language);
    }

    if (Object.keys(user).length === 0) {
      setLanguage(window.location.search?.split('=')[1]);
    }
  }, [user]);

  useMemo(() => {
    setDefaultDependencies({
      mainLanguageAlias: tenant?.mainLanguageAlias,
      language: language,
    });
  }, [tenant?.mainLanguageAlias, language]);

  useEffect(() => {
    if (defaultDependencies?.language && language !== reduxStrings?.language) {
      get(uriGetFunctionalities(tenant?.buDefaultAlias))
        .then((res: any) => {
          const buDefaultFunctionalities = {
            funcionalidade: res?.data?.items || res,
          };
          let stringsToState = {...strings, ...reduxStrings};
          stringsToState = updateStringFeatureWithLabels(
            stringsToState,
            res?.data?.items || res
          );
          setStringsDefault({
            ...stringsToState,
            language: language || tenant?.mainLanguageAlias,
          });
          setBuDefaultFunctionalities(buDefaultFunctionalities);
          let busFuncMap: any = {};
          (res?.data?.items || res).map((func: any) => {
            busFuncMap[func?.featureTemplateAlias] = func?.isVisible;
          });
          setBusFuncMapDefault(busFuncMap);
        })

        .catch((err: any) => {
          setStringsDefault(strings);
        });
    }
  }, [defaultDependencies]);

  useEffect(() => {
    const googleAnalyticsGtm = busFuncMap?.[selectedBu?.buId]?.find(
      (el: any) =>
        el.featureTemplateAlias === functionalitiesAliasMap.googleanalyticsgtm
    )?.isVisible;

    setGoogleAnalyticsGtm(googleAnalyticsGtm);
  }, [busFuncMap, selectedBu?.buId]);

  useEffect(() => {
    googleAnalyticsGtm && ReactGA.initialize('UA-54060744-6');
  }, [googleAnalyticsGtm]);

  useEffect(() => {
    if (googleAnalyticsGtm) {
      const tagManagerArgs = {
        gtmId: 'GTM-K8MX8NF',
        dataLayer: {
          bu: selectedBu.buId,
        },
      };
      selectedBu.buId && TagManager.initialize(tagManagerArgs);
    }
  }, [selectedBu.buId, googleAnalyticsGtm]);

  useEffect(() => {
    setBankAccountDataHolder(
      strings?.ibanRefunds?.ibanRefunds_bankAccountDataHolder
    );
  }, [strings, selectedBu?.famId]);

  useEffect(() => {
    const getMemberData = async () => {
      const memberData: any = await get(
        uriMemberData(selectedBu?.buId, selectedBu?.memId, selectedBu?.famId)
      );
      if (bankAccountDataHolder == 'INDIVIDUAL') {
        setIban(memberData?.iban);
        setTitular(memberData);
      }
    };

    selectedBu?.memId && getMemberData();
  }, [selectedBu?.memId]);

  useEffect(() => {
    setDependenciesForTitular({
      bankAccountDataHolder: bankAccountDataHolder,
      selectedBuFamId: selectedBu?.famId,
    });
  }, [bankAccountDataHolder, selectedBu?.famId]);

  useEffect(() => {
    const getTitular = async () => {
      try {
        let titular: any = await get(
          uriGetTitular(selectedBu?.buId, selectedBu?.famId)
        );
        if (titular) {
          setTitular(titular);
          setIban(titular.iban);
        } else {
          setTitular(null);
          setIban(null);
        }
      } catch (err: any) {
        setTitular(null);
        setIban(null);
      }
    };
    if (bankAccountDataHolder === 'SUBSCRIBER') {
      selectedBu?.famId && getTitular();
    }
  }, [dependenciesForTtular]);

  const anchorCollection = document.getElementsByTagName('a');
  for (let i = 0; i < anchorCollection.length; i++) {
    const anchor = anchorCollection.item(i);

    anchorCollection[i].onmouseover = function () {
      anchorCollection[i].style.opacity = '60%';
    };
    anchorCollection[i].onmouseout = function () {
      anchorCollection[i].style.opacity = '100%';
    };

    if (
      anchor &&
      !anchor?.className.includes('app-footer-anchor') &&
      anchor?.className !== 'recover-email-password' &&
      anchor?.className !== 'register-header-content-exit-label' &&
      anchor?.className !== 'forgot-password-anchor' &&
      anchor?.className !== 'insurance-validation-anchors' &&
      anchor?.className !== 'communications-anchor' &&
      anchor?.className !== 'activate-registration-anchors' &&
      anchor?.className !== 'my-data-content-reimbursement-label' &&
      anchor?.className !== 'pre-authorization-label-anchor' &&
      anchor?.className !== 'reimbursement-header-content-exit-label' &&
      anchor?.className !== 'pre-authorization-header-content-exit-label' &&
      anchor?.className !== 'profile-change-password-anchor' &&
      anchor?.className !== 'exit-label-sucess' &&
      anchor?.className !== 'reimbursement-header-content-exit-label' &&
      anchor?.className !== 'define-iban-anchor-label'
    ) {
      anchor.style.color = globalStyles.primaryColor;
    }
  }

  const inputCollection = document.getElementsByTagName('input');
  for (let i = 0; i < inputCollection.length; i++) {
    inputCollection[i].onclick = function () {
      inputCollection[i].style.backgroundColor = 'white';
    };
    inputCollection[i].onblur = function () {
      inputCollection[i].style.backgroundColor = '#f9f9f9';
    };
  }

  const buttonCollection = document.getElementsByTagName('button');
  for (let i = 0; i < buttonCollection.length; i++) {
    buttonCollection[i].style.fontSize = '17px';
    buttonCollection[i].onmouseover = function () {
      if (!buttonCollection[i].className.includes('login-button'))
        buttonCollection[i].style.opacity = '60%';
    };
    buttonCollection[i].onmouseout = function () {
      buttonCollection[i].style.opacity = '100%';
    };
  }

  const chatbot = document.getElementById('example-widget-container');
  if (chatbot) {
    chatbot.style.display = 'none';
    if (user.state === 'CLIENT') {
      chatbot.style.display = 'block';
    }
  }

  const mainContent = () => {
    if (isUnderMaintenance) {
      return (
        <div className="loading-page-container">
          <PageMaintenance />
        </div>
      );
    } else {
      return (
        <div className="app-wrapper">
          {!loadingInfo ? (
            <RedirectUrlProvider>
              <Routes key={updateRoutesNewSelectedPatient} />
            </RedirectUrlProvider>
          ) : (
            <div className="app-spinner-container">
              <AppSpinner className="spinner-content" />
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <AuthProvider>
      <div
        className="App"
        style={{
          ...globalStyles.background,
        }}>
        {mainContent()}
      </div>
    </AuthProvider>
  );
}

const mapStateToProps = ({
  globalStyles,
  strings,
  selectedBu,
  busStylesMap,
  user,
  busMap,
  isAuthenticated,
  selectedBuReady,
  loadingInfo,
  bankAccountDataHolder,
  updateRoutesNewSelectedPatient,
  busFuncMap,
  busFuncMapDefault,
  tenant,
  language,
  stringsDefault,
  isUnderMaintenance,
}: any) => ({
  globalStyles: globalStyles.value,
  strings: strings.value.pt,
  selectedBu: selectedBu.value,
  busStylesMap: busStylesMap.value,
  user: user.value,
  busMap: busMap.value,
  isAuthenticated: isAuthenticated.value,
  buStyles: globalStyles.value,
  selectedBuReady: selectedBuReady.value,
  loadingInfo: loadingInfo.value,
  bankAccountDataHolder: bankAccountDataHolder.value,
  updateRoutesNewSelectedPatient: updateRoutesNewSelectedPatient.value,
  busFuncMap: busFuncMap.value,
  busFuncMapDefault: busFuncMapDefault.value,
  tenant: tenant.value,
  language: language.value,
  reduxStrings: stringsDefault.value,
  isUnderMaintenance: isUnderMaintenance.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setStrings: (value: any) => dispatch(setStrings(value)),
  setGlobalStyles: (value: any) => dispatch(setGlobalStyles(value)),
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  setTenant: (value: any) => dispatch(setTenant(value)),
  setBuDefaultStyles: (value: any) => dispatch(setBuDefaultStyles(value)),
  setLanguage: (value: any) => dispatch(setLanguage(value)),
  setBankAccountDataHolder: (value: any) =>
    dispatch(setBankAccountDataHolder(value)),
  setIban: (value: any) => dispatch(setIban(value)),
  setTitular: (value: any) => dispatch(setTitular(value)),
  setBusFuncMapDefault: (value: any) => dispatch(setBusFuncMapDefault(value)),
  setStringsDefault: (value: any) => dispatch(setStringsDefault(value)),
  setBuDefaultFunctionalities: (value: any) =>
    dispatch(setBuDefaultFunctionalities(value)),
  setIsUnderMaintenance: (value: any) => dispatch(setIsUnderMaintenance(value)),
  setIsUnderMaintenanceMessage: (value: any) =>
    dispatch(setIsUnderMaintenanceMessage(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps)(App));
